import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { INR } from "@/lib/constants";
import { toast } from "sonner";
import { QrCode, Activity, TrendingUp, AlertTriangle, IndianRupee, Gift, Star, CheckCircle2, Lock } from "lucide-react";

const VOLT = "#CCFF00";

/* --- Utility --- */
function Bar({ value, color = VOLT, height = "h-2" }) {
  return (
    <div className={`w-full ${height} bg-zinc-900 rounded-sm overflow-hidden`}>
      <div className="h-full transition-all duration-500" style={{ width: `${Math.min(100, value)}%`, background: color, boxShadow: color === VOLT ? "0 0 12px rgba(204,255,0,0.4)" : undefined }} />
    </div>
  );
}

function Tile({ children, testid }) {
  return <div className="bg-zinc-950 border border-zinc-900 rounded-sm p-5" data-testid={testid}>{children}</div>;
}

/* --- Coach QR Attendance --- */
function CoachAttendanceCard() {
  const qc = useQueryClient();
  const { user, activeBranchId } = useAuth();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState("");
  const { data: summary } = useQuery({
    queryKey: ["coach-att-summary"],
    queryFn: async () => (await apiClient.get("/coach/attendance/summary")).data,
  });
  const scan = useMutation({
    mutationFn: async () => (await apiClient.post("/coach/attendance/scan", {
      branch_id: activeBranchId || (user.branch_ids || [])[0], qr_token: token.trim(),
    })).data,
    onSuccess: (d) => {
      toast.success(d.already_marked ? "Already marked today" : "Attendance recorded 💪");
      qc.invalidateQueries({ queryKey: ["coach-att-summary"] });
      qc.invalidateQueries({ queryKey: ["coach-metrics"] });
      setOpen(false); setToken("");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Scan failed"),
  });
  const pct = summary?.attendance_pct ?? 0;
  const color = pct >= 90 ? "#00E676" : pct >= 70 ? VOLT : "#FF3B30";
  return (
    <Tile testid="coach-attendance-tile">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
          <Activity size={14} className="text-volt" /> My Attendance · This Month
        </div>
        {summary?.today_marked ? (
          <span className="text-[10px] uppercase tracking-[0.16em] text-[#00E676] font-bold flex items-center gap-1"><CheckCircle2 size={12} /> Today ✓</span>
        ) : null}
      </div>
      <div className="font-display text-6xl font-black leading-none mt-3" style={{ color }} data-testid="coach-attendance-pct">
        {pct}<span className="text-2xl">%</span>
      </div>
      <div className="text-xs text-zinc-500 mt-1">{summary?.days_present || 0} / {summary?.working_days || 0} working days</div>
      <div className="mt-3"><Bar value={pct} color={color} /></div>
      {!summary?.today_marked && (
        <Button size="sm" onClick={() => setOpen(true)} data-testid="scan-branch-qr-btn"
          className="mt-4 w-full bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.18em]">
          <QrCode size={14} className="mr-1" /> Scan Branch QR
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm" data-testid="scan-qr-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">Mark Your Attendance</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">Scan the QR poster at reception or type the token below.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Branch QR Token</Label>
            <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="BRANCH-XXXX-YYYY" data-testid="qr-token-input"
              className="bg-black border-zinc-800 rounded-sm font-mono-cmd mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={() => scan.mutate()} disabled={!token.trim() || scan.isPending} data-testid="confirm-scan"
              className="bg-volt hover:bg-volt-hover text-black rounded-sm">
              <QrCode size={14} className="mr-1" /> Check In
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Tile>
  );
}

/* --- Incentive · Salary · REPS tiles --- */
function IncentiveTile({ amount }) {
  return (
    <Tile testid="coach-incentive-tile">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
        <IndianRupee size={14} className="text-volt" /> Incentive · This Month
      </div>
      <div className="font-display text-5xl font-black text-volt mt-3 leading-none" data-testid="coach-incentive-amount">{INR(amount || 0)}</div>
      <div className="text-xs text-zinc-500 mt-2">Your share from memberships sold at your branch. Actual revenue figure hidden.</div>
    </Tile>
  );
}

function SalaryProgressCard({ salary }) {
  const p = salary || {};
  return (
    <Tile testid="coach-salary-tile">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
        <TrendingUp size={14} className="text-volt" /> Salary Progress
      </div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-display text-4xl font-black text-volt" data-testid="coach-salary-earned">{INR(p.salary_earned || 0)}</div>
        <div className="text-xs text-zinc-500">of {INR(p.salary_monthly || 0)}</div>
      </div>
      <div className="mt-3"><Bar value={p.combined_pct || 0} /></div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <MiniBar label="Days" pct={p.days_pct || 0} />
        <MiniBar label="Sessions" pct={p.sessions_pct || 0} />
        <MiniBar label="Attendance" pct={p.attendance_pct || 0} />
      </div>
    </Tile>
  );
}

function MiniBar({ label, pct }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500 flex justify-between mb-1"><span>{label}</span><span className="text-volt font-bold">{Math.round(pct)}%</span></div>
      <Bar value={pct} height="h-1" />
    </div>
  );
}

function RepsTile({ reps }) {
  const r = reps || {};
  const b = r.breakdown || {};
  return (
    <Tile testid="coach-reps-tile">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
        <Gift size={14} className="text-volt" /> Total REPS
      </div>
      <div className="font-display text-5xl font-black text-volt mt-3 leading-none" data-testid="coach-reps-balance">{r.balance || 0}</div>
      <div className="text-xs text-zinc-500 mt-1">REPS available</div>
      {r.pending_hold > 0 && (
        <div className="mt-3 bg-zinc-900 border border-zinc-800 rounded-sm p-2 flex items-center gap-2" data-testid="reps-pending-hold">
          <Lock size={12} className="text-zinc-500" />
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-400">Pending 6-mo Hold: <span className="text-volt font-bold">{r.pending_hold}</span></div>
        </div>
      )}
      <div className="mt-3 space-y-1 text-[11px] text-zinc-400">
        {b.REFERRAL ? <div className="flex justify-between"><span>Referral conversions</span><span className="text-volt">+{b.REFERRAL}</span></div> : null}
        {b.JOB_REFERRAL ? <div className="flex justify-between"><span>Job referrals</span><span className="text-volt">+{b.JOB_REFERRAL}</span></div> : null}
        {b.ATTENDANCE ? <div className="flex justify-between"><span>Perfect attendance bonus</span><span className="text-volt">+{b.ATTENDANCE}</span></div> : null}
      </div>
    </Tile>
  );
}

function NPSTile({ nps }) {
  const val = nps == null ? null : nps;
  const color = val == null ? "#71717A" : val >= 8 ? "#00E676" : val >= 5 ? VOLT : "#FF3B30";
  return (
    <Tile testid="coach-nps-tile">
      <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-2">
        <Star size={14} className="text-volt" /> Member NPS · 30d
      </div>
      <div className="font-display text-6xl font-black mt-3 leading-none" style={{ color }} data-testid="coach-nps-val">
        {val == null ? "—" : val}
      </div>
      <div className="text-xs text-zinc-500 mt-1">Collected via WhatsApp after each session (Gupshup — mocked until integrated).</div>
    </Tile>
  );
}

/* --- Members at Risk table --- */
function MembersAtRiskPanel({ list }) {
  return (
    <Panel data-testid="coach-at-risk-panel">
      <PanelHeader
        title={`Members at Risk · ${list?.length || 0}`}
        subtitle="Attended ≥ 3 of your sessions last 30d and now below 60% attendance"
      />
      {(!list || list.length === 0) ? (
        <div className="text-zinc-600 text-sm py-8 text-center">No members currently at risk — good job coach.</div>
      ) : (
        <div className="divide-y divide-zinc-900">
          {list.map((m) => (
            <div key={m.user_id} className="py-2 flex items-center gap-3" data-testid={`at-risk-${m.user_id}`}>
              <AlertTriangle size={14} className="text-red-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{m.name}</div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                  {m.last_visit ? `Last visit ${new Date(m.last_visit).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}` : "No recent visit"}
                </div>
              </div>
              <div className="font-display text-xl font-black text-red-500">{Math.round(m.attendance_pct)}%</div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

/* --- Today's sessions to mark conducted --- */
function ConductedSessionsPanel() {
  const qc = useQueryClient();
  const { data: sessions = [] } = useQuery({
    queryKey: ["coach-today-sessions"],
    queryFn: async () => (await apiClient.get("/coach/sessions/today")).data,
  });
  const mark = useMutation({
    mutationFn: async (id) => (await apiClient.post(`/sessions/${id}/mark-conducted`)).data,
    onSuccess: () => {
      toast.success("Session marked as conducted");
      qc.invalidateQueries({ queryKey: ["coach-today-sessions"] });
      qc.invalidateQueries({ queryKey: ["coach-metrics"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  if (sessions.length === 0) return null;
  return (
    <Panel data-testid="today-sessions-panel">
      <PanelHeader title={`Today's Sessions · Pending Mark (${sessions.length})`} subtitle="Tap once you're done — grace window: 24 hours" />
      <div className="space-y-2">
        {sessions.map((s) => (
          <div key={s.id} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3 flex items-center gap-3" data-testid={`session-pending-${s.id}`}>
            <div className="flex-1">
              <div className="font-semibold">{s.title}</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                {new Date(s.start_time).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} · {s.duration_min} min · {s.session_type}
              </div>
            </div>
            <Button size="sm" onClick={() => mark.mutate(s.id)} data-testid={`mark-conducted-${s.id}`}
              className="bg-volt hover:bg-volt-hover text-black rounded-sm text-[10px] uppercase tracking-[0.16em]">
              <CheckCircle2 size={12} className="mr-1" /> Mark Conducted
            </Button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* --- Main Coach Dashboard --- */
export default function CoachDashboard() {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["coach-metrics"],
    queryFn: async () => (await apiClient.get("/coach/dashboard/metrics")).data,
  });
  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="coach-dashboard">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Coach Command · {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Coach, {user?.full_name?.split(" ")[0]}</h1>
          <div className="text-zinc-500 mt-1 text-sm">Your accountability at a glance.</div>
        </div>
      </div>

      <ConductedSessionsPanel />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <CoachAttendanceCard />
        <IncentiveTile amount={data?.incentive?.amount_inr} />
        <SalaryProgressCard salary={data?.salary_progress} />
        <RepsTile reps={data?.reps} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8"><MembersAtRiskPanel list={data?.members_at_risk} /></div>
        <div className="lg:col-span-4"><NPSTile nps={data?.nps_30d} /></div>
      </div>
    </div>
  );
}
